import {
    type PriceCalculationValueWithUnit,
    type BoundRemarksValue,
    type ParameterValueKind,
    type PriceCalculationParameterInfo,
    type ServiceDefinition,
} from "../api";
import { ResolvedParameterValue } from "./ResolvedParameterValue";

/**
 * Represents a remarks/notes value with multiline text input.
 */
export class ResolvedRemarksValue extends ResolvedParameterValue<string, BoundRemarksValue> {
    readonly placeholder?: string;
    readonly maxLength: number;
    readonly help?: string;

    /**
     * Creates a new remarks value with placeholder, max length, and help text bound to a parameter.
     *
     * @param value The remarks configuration
     */
    constructor(value: Omit<BoundRemarksValue, "kind">) {
        super({
            ...value,
            kind: "remarks",
            value: value.value ?? "",
        });
        this.placeholder = value.placeholder;
        this.maxLength = value.maxLength ?? 1000;
        this.help = value.help;
    }

    getKind(): ParameterValueKind {
        return this.kind;
    }

    /**
     * Gets the placeholder text for the remarks field.
     *
     * @returns The placeholder text or undefined if not set
     */
    getPlaceholder(): string | undefined {
        return this.placeholder;
    }

    /**
     * Gets the maximum character length.
     *
     * @returns The maximum length
     */
    getMaxLength(): number {
        return this.maxLength;
    }

    /**
     * Gets the help text for the remarks field.
     *
     * @returns The help text or undefined if not set
     */
    getHelp(): string | undefined {
        return this.help;
    }

    getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[] {
        return [];
    }

    getCalculationParameterValues(_value: string | undefined): Record<string, PriceCalculationValueWithUnit> {
        return {};
    }

    /**
     * Gets the view information for the parameter.
     *
     * @param value The value of the parameter
     * @returns The view information for the parameter
     */
    getViewText(value: string | undefined): string | undefined {
        if (value === undefined) {
            return undefined;
        }
        return value;
    }

    /**
     * Creates a customer-safe version of this value.
     * This value type has no pricing data to strip.
     *
     * @param _requestPricing - The pricing mode (unused - no pricing data in this type)
     * @returns This instance (no changes needed for any pricing mode)
     */
    toCustomerValue(_requestPricing: ServiceDefinition.RequestPricing): this {
        return this;
    }

    /**
     * Creates a new remarks value with placeholder, max length, and help text.
     *
     * @param value The remarks configuration bound to a parameter
     * @returns A new remarks value
     */
    static create(value: Omit<BoundRemarksValue, "kind">): ResolvedRemarksValue {
        return new ResolvedRemarksValue(value);
    }
}
